import type { AssuranceVideo, BulkReviewPayload } from "@unbox/api";
import { ShieldTick } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAssuranceVideoRowsFetcher, useInfiniteAssuranceVideos } from "@/api/assurance/assurance-videos";
import { useBulkReviewVideos } from "@/api/assurance/bulk-review-videos";
import { useReviewVideo } from "@/api/assurance/review-video";
import { useAssuranceVideo } from "@/api/assurance/video";
import { useMerchantRisksList } from "@/api/merchant-risks";
import { SAFETY_POLICY_LABELS, SAFETY_POLICY_PRIORITY, useSafetyPolicies } from "@/api/safety-policies";
import { useScopedMerchantClient } from "@/api/scoped-merchant-client";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { AddRiskModal } from "@/components/assurance/add-risk-bulk-modal";
import { Button } from "@/components/base/buttons/button";
import { FilterPills } from "@/components/filters/filter-pills";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getStandardRiskDescription, getStandardRiskExplanation } from "@/constants/risk-definitions";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useGmvFormatter } from "@/hooks/use-gmv-formatter";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useFilters } from "@/providers/filter-provider";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { error, success } from "@/providers/ToastProvider";
import { formatPostedAgo } from "@/utils/date-conversion";
import { formatCreatorId, formatEvidence, formatViews, resolveCustomRiskAttribution } from "@/utils/formatters";
import { pluralize } from "@/utils/text";
import type { MessageDraft, ReviewSubmission, Risk, VideoItem } from "../../../components/VideoDetail";
import { VideoDetail } from "../../../components/VideoDetail";
import { DetailPanelLoadingSkeleton, FlaggedTabsLoadingSkeleton } from "../../components/FlaggedTabsLoadingSkeleton";
import { buildFlaggedVideoParams } from "../../params";
import { NeedsReviewTable } from "./NeedsReviewTable";
import { spliceVideosFromCache } from "./splice-videos-from-cache";

const dateFormatter = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });

type TabReviewProps = {
  selectedId?: string;
  onSelectId?: (id: string | undefined) => void;
  search: string;
};

export const TabReview = ({ selectedId, onSelectId, search }: TabReviewProps) => {
  const { apiFilters, hasActiveFilters, reset } = useFilters();
  const scope = useScopedMerchantClient();
  // Closing the detail panel (the X button) hides it; selecting a video reopens it.
  // Independent of which video is selected.
  const [detailOpen, setDetailOpen] = useState(true);
  const [addRiskOpen, setAddRiskOpen] = useState(false);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const [messageDrafts, setMessageDrafts] = useState<Record<string, MessageDraft>>({});
  const [editedBulkConfirmOpen, setEditedBulkConfirmOpen] = useState(false);
  const bulkReviewMutation = useBulkReviewVideos();

  const queueParams = { ...buildFlaggedVideoParams(apiFilters, search), category: "pendingManualReview" as const };
  const { videos, total, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteAssuranceVideos(queueParams);

  const {
    selectedKeys: selectedVideoIds,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<AssuranceVideo, { videoId: string }>({
    getKey: useCallback((v: AssuranceVideo) => v.videoId, []),
    toData: useCallback((v: AssuranceVideo) => ({ videoId: v.videoId }), []),
  });
  const handleSelectionChange = useMemo(() => createSelectionHandler(videos), [createSelectionHandler, videos]);

  const fetchChunk = useAssuranceVideoRowsFetcher(queueParams);
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total,
    loadedCount: videos.length,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify(queueParams),
  });
  // TODO(UNB-991): "unable to analyse" videos are cut from Needs Review until the
  // unable-to-access vs unable-to-analyse split lands — re-add an unableToAnalyse
  // query here (mapping rows with unableToAccess: true) once that ticket is done.

  const reviewMutation = useReviewVideo();
  const { data: policies } = useSafetyPolicies();
  const { data: merchantRisks } = useMerchantRisksList();
  const formatGmv = useGmvFormatter();

  // TODO(UNB-1741): tidy this up — risks/actionSteps are hardcoded empty and only
  // hydrated for the selected video, and this list-to-view-model mapping is duplicated
  // across the flagged tabs. Pull it into a shared helper.
  const toVideoItem = (v: (typeof videos)[number], unableToAccess: boolean): VideoItem => ({
    id: v.videoId,
    title: v.videoTitle,
    creator: formatCreatorId(v.creatorId ?? ""),
    creatorId: v.creatorId ?? undefined,
    views: formatViews(v.views) ?? "-",
    postedAt: formatPostedAgo(v.publishDate),
    postedAtDate: dateFormatter.format(new Date(v.publishDate)),
    gmv: formatGmv(v.gmv),
    flagsCountDetail: v.flagsCountDetail,
    topFlags: v.topFlags ? { name: v.topFlags.name, severity: tierToSeverity(v.topFlags.tier ?? 4) } : null,
    flagCount: v.flagCount,
    risks: [],
    actionSteps: [],
    urlFirstFrame: v.urlFirstFrame ?? null,
    isInAdCampaign: v.isInAdCampaign,
    unableToAccess,
  });

  const filtered: VideoItem[] = videos.map((v) => toVideoItem(v, false));

  const resolvedId = selectedId ?? filtered[0]?.id ?? "";
  // No fallback to filtered[0] when selectedId is set: a deep-linked video not yet on a
  // loaded page would otherwise mix its own risks/transcript (fetched for resolvedId below)
  // with the first loaded row's header — two different videos stitched together.
  // isLoadingSelectedVideo covers that gap with a loading state instead.
  const selectedListItem = selectedId ? filtered.find((v) => v.id === selectedId) : filtered[0];
  const isLoadingSelectedVideo = !!selectedId && !selectedListItem && (hasNextPage || isFetchingNextPage);
  const resolvedIndex = selectedListItem ? filtered.indexOf(selectedListItem) : 0;

  // Keep the lazy-loaded buffer ahead of the reviewer as they page through with the
  // detail pane's prev/next, so the "next" video is loaded before they reach the end.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && filtered.length - resolvedIndex < 10) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, filtered.length, resolvedIndex]);

  const { data: detail } = useAssuranceVideo(resolvedId);

  // Deep-link support: when the URL targets a video on a not-yet-loaded page, keep
  // loading (bounded by the dataset) until it appears, instead of silently falling back
  // to the first video. Gated on `detail` so a stale/invalid id doesn't page through
  // the whole list.
  useEffect(() => {
    if (selectedId && detail && !filtered.some((v) => v.id === selectedId) && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [selectedId, detail, filtered, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // The video detail returns dismissed flags too. A flag with any dismissal counts as dismissed,
  // matching the video status the API derives, so it never comes back as a risk to review.
  const activeFlags = (detail?.flags ?? []).filter(
    (flag) => !flag.edits.some((edit) => edit.actionTaken === "flag_dismissed"),
  );

  const risks: Risk[] = activeFlags.map((flag) => {
    const evidence = [
      ...formatEvidence(flag.transcriptEvidence, flag.transcriptEvidenceEn),
      ...formatEvidence(flag.visualEvidence, flag.visualEvidenceEn),
    ];
    if (evidence.length === 0)
      evidence.push({ timestamp: "", text: getStandardRiskExplanation(flag.riskTypeShort) ?? flag.explanation ?? "" });
    return {
      id: flag.id,
      type: flag.riskType,
      title: flag.riskTypeShort,
      evidence,
      reviewReason: flag.reviewReason,
      severity: tierToSeverity(flag.riskTier ?? 4),
      description: getStandardRiskDescription(flag.riskId ?? "") ?? undefined,
      riskId: flag.riskId ?? undefined,
      ...resolveCustomRiskAttribution(flag),
    };
  });

  const detailItem: VideoItem | undefined = selectedListItem
    ? {
        ...selectedListItem,
        risks,
        isQuarantined: detail?.quarantine?.isQuarantined,
        transcript: detail?.transcript,
        detectedLanguage: detail?.detectedLanguage,
        englishTranscript: detail?.englishTranscript,
        videoUrl: detail?.videoUrl,
      }
    : undefined;
  // TODO: Tidy up, way too much being done on the FE here

  // Map each of the video's flags to its configured safety policy + risk model type
  // (via merchant_risks) to drive the "Run policy" dropdown.
  const merchantRiskByRiskId = new Map((merchantRisks ?? []).map((mr) => [mr.riskId, mr]));
  // A risk the reviewer added counts like a detected one, so a more severe added risk raises the
  // suggested policy.
  const videoRiskIds = activeFlags.map((f) => f.riskId).filter((id): id is string => !!id);
  const hasAddedRiskWithRiskId = activeFlags.some((f) => !!f.riskId && resolveCustomRiskAttribution(f).isCustom);
  const requiredTextPresent = videoRiskIds.some(
    (id) => merchantRiskByRiskId.get(id)?.risk.modelType === "required_text",
  );

  // Options: all configured policies, severity-ordered, hiding metadata-edit unless a
  // reviewed risk is required_text (its sequence's branch is meaningless otherwise).
  const policyOptions = (policies ?? [])
    .filter((p) => p.type !== "request_metadata_edit" || requiredTextPresent)
    .sort((a, b) => SAFETY_POLICY_PRIORITY[a.type] - SAFETY_POLICY_PRIORITY[b.type])
    .map((p) => ({ id: p.id, label: SAFETY_POLICY_LABELS[p.type], type: p.type, sequenceId: p.sequenceId }));

  // Pre-select the policy the backend would auto-run: the highest-priority policy
  // mapped to any of the video's risks.
  const mappedPolicyIds = new Set(
    videoRiskIds.map((id) => merchantRiskByRiskId.get(id)?.safetyPolicyId).filter((id): id is string => !!id),
  );
  const defaultPolicyId = (policies ?? [])
    .filter((p) => mappedPolicyIds.has(p.id))
    .sort((a, b) => SAFETY_POLICY_PRIORITY[a.type] - SAFETY_POLICY_PRIORITY[b.type])[0]?.id;

  // Row-exit CSS transition duration (see NeedsReviewTable's row className) — the toast and
  // tab-count update fire only once it's finished, not before.
  const ROW_EXIT_ANIMATION_MS = 300;

  const animateOutThenRun = (videoIds: string[], after: () => void) => {
    setExitingIds((prev) => new Set([...prev, ...videoIds]));
    setTimeout(() => {
      after();
      setExitingIds((prev) => {
        const next = new Set(prev);
        for (const id of videoIds) next.delete(id);
        return next;
      });
    }, ROW_EXIT_ANIMATION_MS);
  };

  const editedSelectedCount = selectedVideoIds.filter((id) => messageDrafts[id]).length;

  const setMessageDraft = (videoId: string, draft: MessageDraft | undefined) =>
    setMessageDrafts(({ [videoId]: _dropped, ...rest }) => (draft ? { ...rest, [videoId]: draft } : rest));

  const discardMessageDrafts = (videoIds: string[]) =>
    setMessageDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !videoIds.includes(id))));

  const runBulkReview = (payload: Omit<BulkReviewPayload, "videoIds">, onReviewed: (reviewedCount: number) => void) => {
    const videoIds = selectedVideoIds;
    const loadedIds = new Set(videos.map((v) => v.videoId));
    const reviewsUnloadedVideos = videoIds.some((id) => !loadedIds.has(id));
    bulkReviewMutation.mutate(
      { videoIds, ...payload },
      {
        onSuccess: () => {
          selectAll.clear();
          discardMessageDrafts(videoIds);
          if (reviewsUnloadedVideos) {
            queryClient.invalidateQueries({ queryKey: ["assurance-video-infinite", scope.merchantId] });
            onReviewed(videoIds.length);
            return;
          }
          animateOutThenRun(videoIds, () => {
            spliceVideosFromCache(queryClient, scope.merchantId, videoIds);
            onReviewed(videoIds.length);
          });
        },
      },
    );
  };

  const handleBulkConfirm = () =>
    runBulkReview(
      {
        actionTaken: "flag_is_correct",
        feedbackReason: "Confirmed via bulk safety review",
        startPolicies: true,
      },
      (count) =>
        success(
          "Risks confirmed and relevant policy triggered",
          {
            description: `Policies are running on ${pluralize(count, "video", "videos")}. Track progress in "With creator to action".`,
          },
          "bottom-right",
        ),
    );

  const handleBulkConfirmSelected = () => {
    if (editedSelectedCount > 0) {
      setEditedBulkConfirmOpen(true);
      return;
    }
    handleBulkConfirm();
  };

  const handleBulkConfirmWithoutPolicies = () =>
    runBulkReview(
      {
        actionTaken: "flag_is_correct",
        feedbackReason: "Confirmed via bulk safety review without running policies",
        logNoActionRequired: true,
      },
      (count) =>
        success(
          "Risks confirmed, no policy run",
          {
            description: `${pluralize(count, "video", "videos")} marked as no action needed. You can find them in "Risk resolved".`,
          },
          "bottom-right",
        ),
    );

  const handleBulkDismiss = () =>
    runBulkReview(
      {
        actionTaken: "flag_dismissed",
        feedbackReason: "Rejected via bulk safety review",
      },
      (count) =>
        success(
          "Risks rejected",
          {
            description: `Risks removed from ${pluralize(count, "video", "videos")} selected and no action was taken.`,
          },
          "bottom-right",
        ),
    );

  // Adding a risk creates a new unreviewed flag — the video stays in Needs Review (or, if a
  // policy was started for it, may now belong elsewhere. Either way it's not something we can
  // decide client-side), so refetch instead of splicing it out of the cache like approve/reject do.
  const handleBulkAddRiskSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["assurance-video", scope.merchantId] });
    queryClient.invalidateQueries({ queryKey: ["assurance-video-infinite", scope.merchantId] });
    selectAll.clear();
    setAddRiskOpen(false);
  };

  const handleSubmit = ({ videoId, riskStates, policyId, logActionType, messageOverrides }: ReviewSubmission) => {
    const flagReviews = Object.entries(riskStates).map(([flagId, state]) => ({
      flagId,
      actionTaken: state.dismissed ? ("flag_dismissed" as const) : ("flag_is_correct" as const),
      feedbackReason: state.dismissed
        ? [state.rejectReason, state.rejectNote].filter(Boolean).join(": ")
        : "Confirmed via safety review",
    }));

    // "Unable to access" videos have no flags to review but are still resolved by logging no action.
    const isLogAction = logActionType === "no_action_required";
    if (flagReviews.length === 0 && !isLogAction) return;

    // A catalog risk the reviewer added stays confirmed, so its policy still runs when every detected
    // risk is dismissed. A free-text one maps to no policy, so it doesn't.
    const nothingToAction =
      !hasAddedRiskWithRiskId && flagReviews.length > 0 && flagReviews.every((r) => r.actionTaken === "flag_dismissed");

    // Log-only resolutions (e.g. "unable to access") record the action and run no policy.
    const actions = isLogAction ? [{ actionType: "no_action_required" as const, kind: "log" as const }] : undefined;
    // Confirming any risk runs the chosen safety policy. Nothing runs when there's nothing to action
    // or the video was resolved with a log action.
    const startPolicies = !isLogAction && !nothingToAction;

    reviewMutation.mutate(
      {
        videoId,
        flagReviews,
        actions,
        startPolicies,
        policyId: startPolicies ? policyId : undefined,
        // Only a policy run delivers the message, so edits are meaningless without one.
        messageOverrides: startPolicies ? messageOverrides : undefined,
      },
      {
        onSuccess: () => {
          discardMessageDrafts([videoId]);
          // Move to the next video before the reviewed one drops out of the list
          const next = filtered[resolvedIndex + 1] ?? filtered[resolvedIndex - 1];
          onSelectId?.(next && next.id !== videoId ? next.id : undefined);
          const creatorName = filtered.find((v) => v.id === videoId)?.creator;
          animateOutThenRun([videoId], () => {
            spliceVideosFromCache(queryClient, scope.merchantId, [videoId]);
            if (startPolicies && policyId) {
              const policy = (policies ?? []).find((p) => p.id === policyId);
              success(
                `${policy ? SAFETY_POLICY_LABELS[policy.type] : "Policy"} — started`,
                {
                  description: `${creatorName ?? "Creator"}'s video moved to "With creator to action". You can track the policy's progress there.`,
                },
                "bottom-right",
              );
            } else {
              success("Review submitted", {}, "bottom-right");
            }
          });
        },
        onError: () => {
          error("Failed to submit review");
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 flex-1 min-h-0">
        <FilterPills className="-my-2" />
        <FlaggedTabsLoadingSkeleton
          gapClassName="gap-8"
          listClassName="w-[calc(44%-1rem)] shrink-0 h-full"
          detailClassName="flex-1 h-full w-[calc(66%-1rem)]"
        />
      </div>
    );
  }

  const isTrulyEmpty = filtered.length === 0 && !hasActiveFilters && !search;

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <FilterPills className="-my-2" />
      {isTrulyEmpty ? (
        <div className="w-full flex-1 flex items-center justify-center rounded-md border border-border-primary bg-white">
          <UnboxTableEmptyStateContent
            rows={[]}
            emptyState={{
              icon: ShieldTick,
              title: "No videos to review",
              description: "All active risks are either resolved or being actioned",
            }}
          />
        </div>
      ) : (
        <div className="flex flex-row gap-8 w-[100%] flex-1 min-h-0">
          <NeedsReviewTable
            videos={filtered}
            total={total}
            selectedId={selectedListItem?.id ?? ""}
            onSelect={(id) => {
              onSelectId?.(id);
              setDetailOpen(true);
            }}
            hasActiveFilters={hasActiveFilters || !!search}
            onClearFilters={reset}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            fullWidth={!detailOpen}
            selected={selectedVideoIds}
            onSelectionChange={handleSelectionChange}
            selectedCount={selectedCount}
            selectAll={selectAll}
            onConfirm={handleBulkConfirmSelected}
            onConfirmWithoutPolicies={handleBulkConfirmWithoutPolicies}
            onDismiss={handleBulkDismiss}
            onAddRisk={() => setAddRiskOpen(true)}
            bulkActionsDisabled={bulkReviewMutation.isPending || exitingIds.size > 0 || selectAll.isFetching}
            exitingIds={exitingIds}
          />
          <Dialog open={editedBulkConfirmOpen} onOpenChange={setEditedBulkConfirmOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Edited messages won't be sent</DialogTitle>
                <DialogDescription>
                  {pluralize(editedSelectedCount, "selected video has", "selected videos have")} a message you edited.
                  Confirming in bulk sends each policy's default message instead. To send your edits, confirm those
                  videos one at a time.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button color="secondary" size="sm" onClick={() => setEditedBulkConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    setEditedBulkConfirmOpen(false);
                    handleBulkConfirm();
                  }}
                >
                  Send default messages
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <AddRiskModal
            selectedVideos={selectedVideoIds}
            onSuccess={handleBulkAddRiskSuccess}
            open={addRiskOpen}
            onOpenChange={setAddRiskOpen}
          />
          {detailOpen &&
            (detailItem ? (
              <VideoDetail
                video={detailItem}
                current={resolvedIndex + 1}
                total={total}
                onPrev={() => {
                  if (resolvedIndex > 0) onSelectId?.(filtered[resolvedIndex - 1].id);
                }}
                onNext={() => {
                  if (resolvedIndex < filtered.length - 1) onSelectId?.(filtered[resolvedIndex + 1].id);
                  // At the last loaded item with more still to come, pull the next page so the
                  // enabled Next button isn't a no-op while the buffer catches up.
                  else if (hasNextPage && !isFetchingNextPage) fetchNextPage();
                }}
                onClose={() => setDetailOpen(false)}
                onSubmit={handleSubmit}
                isSubmitting={reviewMutation.isPending}
                policyOptions={policyOptions}
                defaultPolicyId={defaultPolicyId}
                messageDraft={messageDrafts[detailItem.id]}
                onMessageDraftChange={(draft) => setMessageDraft(detailItem.id, draft)}
              />
            ) : isLoadingSelectedVideo ? (
              <DetailPanelLoadingSkeleton className="flex-1 h-full" />
            ) : (
              <div className="flex-1 flex items-center justify-center rounded-lg border border-border-primary bg-white min-h-[300px]">
                <UnboxTableEmptyStateContent rows={[]} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
